import { useState, useMemo } from 'react';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Search, ChevronRight, ArrowLeft } from 'lucide-react';
import { useOptiBayFamilies, useOptiBaySubfamilies } from '@/hooks/useOptiBayTaxonomy';
import { cn } from '@/lib/utils';
import type { OptiBaySession } from '@/types/optibay';

interface OptiBaySystemFamilyStepProps {
  session: OptiBaySession;
  onSelect: (familyId: string, subfamilyId: string | null) => void;
  onBack: () => void;
}

export function OptiBaySystemFamilyStep({ session, onSelect, onBack }: OptiBaySystemFamilyStepProps) {
  const [search, setSearch] = useState('');
  const [selectedFamilyId, setSelectedFamilyId] = useState<string | null>(session.system_family_id);

  const { data: families = [], isLoading: loadingFamilies } = useOptiBayFamilies();
  const { data: subfamilies = [], isLoading: loadingSubfamilies } = useOptiBaySubfamilies(selectedFamilyId);

  const filteredFamilies = useMemo(() => {
    if (!search.trim()) return families;
    const q = search.toLowerCase();
    return families.filter(f => f.label.toLowerCase().includes(q) || f.key.toLowerCase().includes(q));
  }, [families, search]);

  const selectedFamily = families.find(f => f.id === selectedFamilyId);

  if (selectedFamilyId && selectedFamily) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={() => setSelectedFamilyId(null)} className="h-7 px-2">
            <ArrowLeft className="h-3.5 w-3.5 mr-1" />
            Back
          </Button>
          <h3 className="font-semibold text-sm">{selectedFamily.label}</h3>
          {selectedFamily.category_key && (
            <Badge variant="secondary" className="text-xs">{selectedFamily.category_key}</Badge>
          )}
        </div>

        {loadingSubfamilies ? (
          <div className="text-sm text-muted-foreground py-4 text-center">Loading subfamilies…</div>
        ) : subfamilies.length > 0 ? (
          <div className="grid grid-cols-2 gap-2">
            {subfamilies.map(sub => (
              <button
                key={sub.id}
                type="button"
                onClick={() => onSelect(selectedFamilyId, sub.id)}
                className={cn(
                  'text-left rounded-lg border p-3 hover:bg-accent/50 transition-colors',
                  session.subfamily_id === sub.id && 'border-primary bg-primary/5'
                )}
              >
                <span className="text-sm font-medium">{sub.label}</span>
              </button>
            ))}
            <button
              type="button"
              onClick={() => onSelect(selectedFamilyId, null)}
              className="text-left rounded-lg border border-dashed p-3 hover:bg-accent/50 transition-colors text-muted-foreground"
            >
              <span className="text-sm">General / No Subfamily</span>
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">No subfamilies defined for this family.</p>
            <Button onClick={() => onSelect(selectedFamilyId, null)}>
              Continue with {selectedFamily.label} →
            </Button>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="sm" onClick={onBack} className="h-7 px-2">
          <ArrowLeft className="h-3.5 w-3.5 mr-1" />
          Back
        </Button>
        <h3 className="font-semibold text-sm">Select System Family</h3>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search system families…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="pl-9"
        />
      </div>

      {loadingFamilies ? (
        <div className="text-sm text-muted-foreground py-4 text-center">Loading families…</div>
      ) : (
        <div className="grid grid-cols-2 gap-2 max-h-[400px] overflow-y-auto">
          {filteredFamilies.map(fam => (
            <button
              key={fam.id}
              type="button"
              onClick={() => setSelectedFamilyId(fam.id)}
              className={cn(
                'text-left rounded-lg border p-3 hover:bg-accent/50 transition-colors group',
                session.system_family_id === fam.id && 'border-primary bg-primary/5'
              )}
            >
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">{fam.label}</span>
                <ChevronRight className="h-3.5 w-3.5 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
              </div>
              {fam.category_key && (
                <span className="text-xs text-muted-foreground">{fam.category_key}</span>
              )}
            </button>
          ))}
          {filteredFamilies.length === 0 && (
            <div className="col-span-2 text-center text-sm text-muted-foreground py-4">
              No matching system families
            </div>
          )}
        </div>
      )}
    </div>
  );
}
